import { AxiosProgressEvent, AxiosResponse, AxiosStatic } from 'axios';

import { InfraStrukturDB } from 'boot/queryStoreAndCommandBus';
import { strToU8, zlibSync } from 'fflate';
import { IDBPDatabase } from 'idb';

import { AbstractCommand } from 'src/models/command';

import { CommandBuilder } from 'src/util/command/CommandBuilder';
import { QueryStoreOfflineDatabase } from 'src/util/query/QueryStoreOfflineDatabase';

type CommandParametersRecord = Extract<AbstractCommand['parameters'], Record<string, unknown>>;

interface SendCommandOptions {
	onUploadProgress?: (percent: number) => void;
}

const commandParametersRecord = (parameters: AbstractCommand['parameters']): CommandParametersRecord | null => {
	return Array.isArray(parameters) ? null : parameters;
};

const progressHandler = (options?: SendCommandOptions): ((event: AxiosProgressEvent) => void) | undefined => {
	const onUploadProgress = options?.onUploadProgress;
	if (onUploadProgress === undefined) {
		return undefined;
	}

	return (event: AxiosProgressEvent): void => {
		if (!event.total) {
			return;
		}

		onUploadProgress(Math.round((event.loaded / event.total) * 100));
	};
};

class CommandBus {
	private readonly appVersion: string;
	private axios: AxiosStatic;
	private commandBuilder: CommandBuilder;
	private readonly database: IDBPDatabase<InfraStrukturDB> | null;
	private readonly instanceName: string;
	private queryStoreOfflineDatabase: QueryStoreOfflineDatabase;
	private readonly user: string;

	constructor(
		commandBuilder: CommandBuilder,
		axios: AxiosStatic,
		database: IDBPDatabase<InfraStrukturDB> | null,
		instanceName: string,
		user: string,
		appVersion: string
	) {
		this.commandBuilder = commandBuilder;
		this.axios = axios;
		this.database = database;
		this.instanceName = instanceName;
		this.user = user;
		this.appVersion = appVersion;
		this.queryStoreOfflineDatabase = new QueryStoreOfflineDatabase(
			this.database,
			this.instanceName,
			this.user,
			this.appVersion
		);
	}

	public async sendCommand<T = never>(
		command: AbstractCommand,
		options?: SendCommandOptions
	): Promise<AxiosResponse<T> | null> {
		let response: AxiosResponse<T> | null = null;
		const parameters = commandParametersRecord(command.parameters);

		if (parameters !== null && typeof parameters.aggregateId === 'string') {
			void this.queryStoreOfflineDatabase.removeByAggregateId(parameters.aggregateId);
		}

		if (parameters !== null && Array.isArray(parameters.aggregateIds)) {
			parameters.aggregateIds.forEach((aggregateId) => {
				if (typeof aggregateId !== 'string') {
					return;
				}

				void this.queryStoreOfflineDatabase.removeByAggregateId(aggregateId);
			});
		}
		try {
			const compressedCommand = zlibSync(
				strToU8(JSON.stringify(Object.fromEntries(this.commandBuilder.build(command))))
			);

			response = await this.axios.post<T>(this.commandBuilder.commandBusURL, compressedCommand, {
				headers: {
					'Content-Type': 'multipart/form-data',
				},
				maxBodyLength: Infinity,
				maxContentLength: Infinity,
				onUploadProgress: progressHandler(options),
			});
		} catch (error: Error | unknown) {
			if (!this.axios.isCancel(error)) {
				//console.error('Command failed', command.name, error);
			}
		}

		return response;
	}
}

export { CommandBus };
